import { useSimFileValidation } from "../../../hooks/useSimFileValidation";
import SimValidationSummary from "../../../components/SimValidationSummary";
import { Button, Collapse, Form, Spinner } from "react-bootstrap";
import { useWhitelabel } from "../../../contexts/WhitelabelContext";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

const SimImportSettingsForm = (props) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    isDesktop,
  } = props;

  const { backgroundColor, fontColor } = useWhitelabel();
  const { t } = useTranslation();
  const [providerCollapse, setProviderCollapse] = useState(null);
  const [inputTypeCollapse, setInputTypeCollapse] = useState(null);

  const { validating, report, error, validateFile, reset } =
    useSimFileValidation();

  // Clear validation if key inputs change
  useEffect(() => {
    reset();
  }, [values.provider, values.simType, values.inputType]); // eslint-disable-line

  useEffect(() => {
    setProviderCollapse(null);
    const id = setTimeout(() => setProviderCollapse(values.provider), 400);
    return () => clearTimeout(id);
  }, [values.provider]);

  useEffect(() => {
    setInputTypeCollapse(null);
    const id = setTimeout(() => setInputTypeCollapse(values.inputType), 400);
    return () => clearTimeout(id);
  }, [values.inputType]);

  // Client-only quick checks for single ICCID

  const onFileChange = async (event) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    // Reset the input to allow same-file reselect
    event.target.value = "";

    setFieldValue("file", file);
    if (values.provider && values.simType) {
      await validateFile({
        file,
        provider: values.provider,
        simType: values.simType,
        action: "import",
      });
    }
  };

  const submitDisabled =
    values.inputType === "file"
      ? !report || validating || !report.valid
      : false;

  return (
    <Form onSubmit={handleSubmit}>
      {/* Input type */}
      <Form.Group className="mb-3">
        <Form.Label>{t("settings.importSimSettings.inputType")}</Form.Label>
        <Form.Select
          name="inputType"
          value={values.inputType}
          onChange={handleChange}
          onBlur={handleBlur}
          isInvalid={Boolean(touched.inputType && errors.inputType)}
        >
          <option value="">
            {t("settings.importSimSettings.selectInputType")}
          </option>
          <option value="single">
            {t("settings.importSimSettings.single")}
          </option>
          <option value="file">{t("settings.importSimSettings.file")}</option>
        </Form.Select>
      </Form.Group>

      {/* Provider */}
      <Form.Group className="mb-3">
        <Form.Label>{t("settings.importSimSettings.provider")}</Form.Label>
        <Form.Select
          name="provider"
          value={values.provider}
          onChange={handleChange}
          onBlur={handleBlur}
          isInvalid={Boolean(touched.provider && errors.provider)}
        >
          <option value="">
            {t("settings.importSimSettings.selectProvider")}
          </option>
          <option value="Velos">Velos IoT</option>
          <option value="1NCE">1NCE</option>
        </Form.Select>
        {!!touched.provider && (
          <Form.Control.Feedback type="invalid">
            {errors.provider}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      {/* Velos - SINGLE */}
      <Collapse
        in={providerCollapse === "Velos" && inputTypeCollapse === "single"}
        dimension="height"
      >
        <div>
          <Form.Group className="mb-3">
            <Form.Label>SIM Type</Form.Label>
            <Form.Select
              name="simType"
              value={values.simType}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={Boolean(touched.simType && errors.simType)}
            >
              <option value="">Select SIM Type</option>
              <option value="NextM2M Sim T1">NextM2M Sim T1</option>
              <option value="NextM2M Sim T2">NextM2M Sim T2</option>
              <option value="NextM2M Sim T3">NextM2M Sim T3</option>
              <option value="Jersey Embedded Sim">Jersey Embedded Sim</option>
            </Form.Select>
            {!!touched.simType && (
              <Form.Control.Feedback type="invalid">
                {errors.simType}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>ICCID</Form.Label>
            <Form.Control
              type="text"
              name="iccid"
              value={values.iccid}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={Boolean(touched.iccid && errors.iccid)}
            />
            {!!touched.iccid && errors.iccid && (
              <Form.Control.Feedback type="invalid">
                {errors.iccid}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </div>
      </Collapse>

      {/* Velos - FILE */}
      <Collapse
        in={providerCollapse === "Velos" && inputTypeCollapse === "file"}
        dimension="height"
      >
        <div>
          <Form.Group className="mb-3">
            <Form.Label>SIM Type</Form.Label>
            <Form.Select
              name="simType"
              value={values.simType}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={Boolean(touched.simType && errors.simType)}
            >
              <option value="">Select SIM Type</option>
              <option value="NextM2M Sim T1">NextM2M Sim T1</option>
              <option value="NextM2M Sim T2">NextM2M Sim T2</option>
              <option value="NextM2M Sim T3">NextM2M Sim T3</option>
              <option value="Jersey Embedded Sim">Jersey Embedded Sim</option>
            </Form.Select>
            {!!touched.simType && (
              <Form.Control.Feedback type="invalid">
                {errors.simType}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>File Upload</Form.Label>
            <Form.Control
              key={Date.now()}
              type="file"
              size="lg"
              className="sim-file-upload"
              isInvalid={!!errors.file}
              onChange={onFileChange}
            />
            {validating && (
              <div className="mt-2 d-flex align-items-center gap-2">
                <Spinner size="sm" animation="border" />{" "}
                <span>Validating file…</span>
              </div>
            )}
          </Form.Group>

          {/* Validation result card */}
          <SimValidationSummary report={report} error={error} />
        </div>
      </Collapse>

      {/* 1NCE - FILE (same pattern) */}
      <Collapse
        in={providerCollapse === "1NCE" && inputTypeCollapse === "file"}
        dimension="height"
      >
        <div>
          <Form.Group className="mb-3">
            <Form.Label>SIM Type</Form.Label>
            <Form.Select
              name="simType"
              value={values.simType}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={Boolean(touched.simType && errors.simType)}
            >
              <option value="">Select SIM Type</option>
              <option value="IoT SIM Card Business">
                IoT SIM Card Business
              </option>
              <option value="IoT SIM Card Industrial">
                IoT SIM Card Industrial
              </option>
              <option value="IoT SIM Chip Industrial">
                IoT SIM Chip Industrial
              </option>
            </Form.Select>
            {!!touched.simType && (
              <Form.Control.Feedback type="invalid">
                {errors.simType}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>File Upload</Form.Label>
            <Form.Control
              key={Date.now()}
              type="file"
              size="lg"
              className="sim-file-upload"
              isInvalid={!!errors.file}
              onChange={onFileChange}
            />
            {validating && (
              <div className="mt-2 d-flex align-items-center gap-2">
                <Spinner size="sm" animation="border" />{" "}
                <span>Validating file…</span>
              </div>
            )}
          </Form.Group>

          <SimValidationSummary report={report} error={error} />
        </div>
      </Collapse>

      {/* 1NCE - SINGLE */}
      <Collapse
        in={providerCollapse === "1NCE" && inputTypeCollapse === "single"}
        dimension="height"
      >
        <div>
          {/* ... (unchanged SIM type select) ... */}
          <Form.Group className="mb-3">
            <Form.Label>ICCID</Form.Label>
            <Form.Control
              type="text"
              name="iccid"
              value={values.iccid}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={Boolean(touched.iccid && errors.iccid)}
            />
            {!!touched.iccid && errors.iccid && (
              <Form.Control.Feedback type="invalid">
                {errors.iccid}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>SIM Type</Form.Label>
            <Form.Select
              name="simType"
              value={values.simType}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={Boolean(touched.simType && errors.simType)}
            >
              <option value="">Select SIM Type</option>
              <option value="IoT SIM Card Industrial">
                IoT SIM Card Industrial
              </option>
              <option value="IoT SIM Card Business">
                IoT SIM Card Business
              </option>
            </Form.Select>
            {!!touched.simType && (
              <Form.Control.Feedback type="invalid">
                {errors.simType}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </div>
      </Collapse>

      {/* Submit button */}
      <div
        className={`d-flex ${
          isDesktop ? "justify-content-end" : "justify-content-center"
        } mt-3`}
      >
        <Button
          type="submit"
          variant="primary"
          style={{
            "--dynamic-bg-color": backgroundColor,
            "--dynamic-font-color": fontColor,
            border: "none",
            ...(isDesktop ? {} : { width: "100%", padding: "10px" }),
          }}
          disabled={submitDisabled}
          title={
            values.inputType === "file"
              ? !report
                ? "Please upload a file and validate"
                : report.valid
                ? ""
                : "Fix validation issues to proceed"
              : "Fix input errors to proceed"
          }
        >
          {validating ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />{" "}
              {t("settings.importSimSettings.submit")}
            </>
          ) : (
            t("settings.importSimSettings.submit")
          )}
        </Button>
      </div>
    </Form>
  );
};

export default SimImportSettingsForm;
